import { createToaster } from 'sonner-vanilla';
import { submitCheckout } from './api';
import { HOOKS } from './constants';
import { dom } from './dom';
import { hooks } from './hooks';
import { createUI } from './ui';
import { validateGuest } from './validation';

createToaster({
	position: 'bottom-right',
	theme: 'light',
	richColors: true,
	closeButton: true,
}).mount(document.body);

declare const __ALLCOACH_CHECKOUT__: {
	nonce: string;
	currentUserId: number;
	userEmail: string;
	checkoutEndpoint: string;
	guestEndpoint: string;
	i18n: {
		firstNameRequired: string;
		lastNameRequired: string;
		emailRequired: string;
		emailInvalid: string;
		genericError: string;
		processing: string;
		placeOrder: string;
		orderReceived: string;
		tyMessage: string;
		tyNote: string;
		orderId: string;
		program: string;
		payment: string;
		status: string;
		total: string;
	};
};

export function initCheckout() {
	const form = dom.form;
	if (!form) return;

	const { currentUserId, userEmail, i18n } = __ALLCOACH_CHECKOUT__;

	const isGuest = !Number(currentUserId);
	const UI = createUI(i18n, userEmail);

	hooks.addFilter(HOOKS.VALIDATION, 'allcoach/default', (errors) => {
		return isGuest ? { ...errors, ...validateGuest(i18n) } : errors;
	});

	hooks.addAction(HOOKS.VALIDATION_FAILED, 'allcoach/ui', (errors) => {
		UI.clearFieldErrors();

		for (const [field, msg] of Object.entries(errors)) {
			UI.showFieldError(field, msg as string);
		}
	});

	hooks.addAction(HOOKS.ERROR, 'allcoach/ui', (err: any) => {
		const errors = err?.data?.additional_data?.errors;

		if (errors && typeof errors === 'object') {
			const messages = Object.values(errors) as string[];

			UI.showError(messages.join(', '));

			for (const [field, msg] of Object.entries(errors)) {
				UI.showFieldError(field, msg as string);
			}
		} else {
			const message = err?.message || err?.data?.message || i18n.genericError;
			UI.showError(message);
		}
	});

	hooks.addAction(HOOKS.SUCCESS, 'allcoach/ui', (order) => {
		UI.showThankYou(order);
	});

	hooks.addAction(HOOKS.BEFORE_SUBMIT, 'allcoach/ui', () => {
		UI.hideError();
		UI.clearFieldErrors();
		UI.setLoading(true);
	});

	hooks.addAction(HOOKS.FINALLY, 'allcoach/ui', () => {
		UI.setLoading(false);
	});

	form.addEventListener('submit', async (e) => {
		e.preventDefault();

		hooks.doAction(HOOKS.BEFORE_SUBMIT, form);

		let errors = hooks.applyFilters(HOOKS.VALIDATION, {}, form) as Record<
			string,
			unknown
		>;

		if (Object.keys(errors).length) {
			hooks.doAction(HOOKS.VALIDATION_FAILED, errors, form);
			return;
		}

		try {
			const endpoint = isGuest
				? 'allcoach/v1/orders/guest-checkout'
				: 'allcoach/v1/orders/checkout';

			let body: Record<string, unknown> = {
				notes:
					form.querySelector<HTMLTextAreaElement>('[name="notes"]')?.value ||
					'',
			};

			if (isGuest) {
				body.first_name =
					form.querySelector<HTMLInputElement>('[name="first_name"]')?.value ??
					'';
				body.last_name =
					form.querySelector<HTMLInputElement>('[name="last_name"]')?.value ??
					'';
				body.email =
					form.querySelector<HTMLInputElement>('[name="email"]')?.value ?? '';
				body.phone =
					form.querySelector<HTMLInputElement>('[name="phone"]')?.value ?? '';
				body.program_id = Number(
					form.querySelector<HTMLInputElement>('[name="program_id"]')?.value ??
						0,
				);
			}

			const json = (await submitCheckout(endpoint, body)) as any;
			const order = json.data?.orders?.[0] || json.data;

			hooks.doAction(HOOKS.SUCCESS, order, form);
		} catch (err) {
			hooks.doAction(HOOKS.ERROR, err, form);
		} finally {
			hooks.doAction(HOOKS.FINALLY, form);
		}
	});
}
